import {
  adjustPriceToTickSize,
  asksTickRoundingMode,
  bidsTickRoundingMode,
} from '@katanaperps/katana-perps-sdk';
import JSBI from 'jsbi';
import React, { useEffect, useState, useCallback, useMemo } from 'react';
import styled, { css } from 'styled-components';

import { useMarketQuery } from '#/api/queries/marketsQueries';
import { useActiveWindowInterval } from '#/components/hooks';
import { isIntermittentMarket } from '#/components/markets/intermittent/intermittentMarkets';
// import { BUYSELL_MOBILE_SCROLL_CLASS } from '#/components/trading/BuySellPanel/layout';
import { EXCHANGE_DECIMALS } from '#/config/constants';
import { BigNumberJs } from '#/lib/BigNumber';
import { formatPercentage, formatPrice } from '#/lib/display/formatting';
import { pipToDecimal } from '#/lib/numbers';
import { redirectTo } from '#/lib/redirect';
import {
  calculateTickOrStepSize,
  getDashboardUrl,
  useSelectMarketStepSize,
  useSelectMarketTickSize,
} from '#/reduxState/markets/listed/listedMarketsUtils';
import { useCurrentTickSize } from '#/reduxState/markets/userTickSize/userTickSizeUtils';
import { prefillOrderInputPrice } from '#/reduxState/orders/prefillOrderInputs';
import { useSelector, staticDispatch } from '#/reduxState/store';

import { useFetchAndSyncCurrentMarketOrderPrices } from '../useFetchAndSyncOrdersHook';

import AggregationSelection from './AggregationSelection';
import OrderbookRow from './OrderBookRow';
import {
  BIDS_WRAP_ID,
  ROW_HEIGHT,
  getOrderbookSideRowsCount,
} from './orderBookRowUtils';
import {
  connectToOrderbookAsync,
  disconnectFromOrderbook,
  getSpreadFromDisplayedOrderbook,
  useDisplayedOrderbook,
} from './orderBookService';
import { LevelTwoOrderBookRow } from './orderBookServiceUtils';
import { useHoveredRow, useSummaryLists } from './summaryHooks';
import SummaryTooltip from './SummaryTooltip';

const disabledSectionCss = css<{ disabled: boolean }>`
  ${({ disabled }) =>
    disabled
      ? `
      opacity: 0.6;
      pointer-events: none;`
      : ''}
`;

const Bids = styled.div<{ disabled: boolean; $height: number }>`
  height: ${({ $height }) => $height}px;
  position: relative;
  overflow: hidden;
  /* Prevent Chrome auto scroll after new rows added */
  /* overflow-anchor: none; */
  ${disabledSectionCss}
`;

const BidsAbsolute = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
`;

const Asks = styled.div<{ disabled: boolean; $height: number }>`
  height: ${({ $height }) => $height}px;
  position: relative;
  overflow: hidden;
  /* overflow-anchor: none; */
  ${disabledSectionCss}
`;

const AsksAbsolute = styled.div`
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
`;

const SpreadSeparator = styled.div<{ $minimal: boolean }>`
  border: none;
  padding-left: 12px;
  line-height: 26px;
  font-size: var(--small);
  color: var(--gray4b);
  font-family: var(--digitsFontFamily);

  display: flex;
  justify-content: space-between;

  ${({ $minimal }) =>
    $minimal
      ? `
    padding-left: 8px;
  `
      : ''}
`;

const SpreadComponent = ({
  spreadPip,
  spreadPercentage,
  visibleDecimals,
  loading,
  minimalLayout,
}: {
  spreadPip: JSBI | undefined;
  spreadPercentage: number | undefined;
  visibleDecimals: number | undefined;
  loading: boolean;
  minimalLayout: boolean;
}) => {
  const tickSize = useCurrentTickSize();

  return (
    <SpreadSeparator $minimal={minimalLayout}>
      <div>
        {loading ? (
          <>&nbsp;</>
        ) : spreadPercentage === undefined ? (
          '-'
        ) : (
          <>
            {spreadPip && visibleDecimals !== undefined
              ? formatPrice(pipToDecimal(spreadPip), visibleDecimals)
              : 0}{' '}
            / {formatPercentage(spreadPercentage)}%
          </>
        )}
      </div>
      {tickSize && (
        <AggregationSelection
          defaultTickSize={tickSize.default}
          selected={tickSize.selected}
          maximumTickSize={tickSize.maximumTickSize}
          onChange={tickSize.onChange}
          minimalLayout={minimalLayout}
        />
      )}
    </SpreadSeparator>
  );
};

const SpreadComponentMemoized = React.memo(SpreadComponent);

const OrderbookTableBody = ({
  userTickSizeInDecimals,
  summaryTooltipEnabled,
  minimalLayout,
}: {
  userTickSizeInDecimals: string;
  summaryTooltipEnabled: boolean;
  minimalLayout: boolean;
}) => {
  const isMobile = useSelector(state => state.screen.isMobile);
  const verticalResolutionIndex = useSelector(
    state => state.screen.verticalResolutionIndex
  );
  const currentMarket = useSelector(state => state.markets.currentMarket);
  const marketTickSize = useSelectMarketTickSize(currentMarket.pair);
  const marketTickSizeInPip = marketTickSize?.inPip;
  const marketTickSizeVisibleDecimals = marketTickSize?.visibleDecimals;
  const loggedIn = useSelector(state => state.currentUser.loggedIn);
  const userTickSize = useMemo(
    () => calculateTickOrStepSize(userTickSizeInDecimals),
    [userTickSizeInDecimals]
  );
  const marketStepSizeDecimals =
    useSelectMarketStepSize(currentMarket.pair)?.visibleDecimals ??
    EXCHANGE_DECIMALS;
  const walletAddress = useSelector(state => state.wallet.walletAddress);

  const [orders, setOrders] = useState<{
    initialized: boolean;
    asks: LevelTwoOrderBookRow[];
    bids: LevelTwoOrderBookRow[];
  }>({
    initialized: false,
    asks: [],
    bids: [],
  });

  const { serviceExists } = useDisplayedOrderbook();

  const { data: marketData } = useMarketQuery(currentMarket.pair);

  // On the mobile trade page the session banner makes the BuySell panel
  // taller — fill the resulting free space with extra rows
  const showsSessionBanner =
    minimalLayout && isIntermittentMarket(marketData?.type);

  const orderbookSideRowsCount = getOrderbookSideRowsCount(
    verticalResolutionIndex,
    showsSessionBanner
  );
  const orderbookSideHeightPx = ROW_HEIGHT * orderbookSideRowsCount;

  // Limit to visible count
  const asks = orders.asks.slice(-orderbookSideRowsCount);
  const bids = orders.bids.slice(0, orderbookSideRowsCount);

  useEffect(() => {
    // if (isMobile) {
    //   // On Mobile we subscribe to orderbook on dashboard and couple extra pages globally
    //   return;
    // }
    if (!userTickSizeInDecimals || marketTickSizeInPip === undefined) {
      return;
    }
    if (currentMarket.pair) {
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setOrders(state => ({ ...state, initialized: false }));
      const controller = new AbortController();
      connectToOrderbookAsync(currentMarket.pair, controller.signal, {
        type: 'orderbook',
        defaultMarketTickSizeInPip: marketTickSizeInPip,
        onChange: (asks, bids, isReset) =>
          setOrders({ asks, bids, initialized: !isReset }),
        stepSizeVisibleDecimals: marketStepSizeDecimals,
        customTickSizeInDecimals: userTickSizeInDecimals,
      });
      return () => {
        disconnectFromOrderbook('orderbook', controller);
      };
    }
  }, [
    currentMarket.pair,
    marketTickSizeInPip,
    userTickSizeInDecimals,
    marketStepSizeDecimals,
    isMobile,
  ]);

  // const separatorRef = useRef<HTMLDivElement>(null);
  // const scrollIntoCenter = useCallback(() => {
  //   if (!separatorRef.current) {
  //     return;
  //   }
  //   separatorRef.current.scrollIntoView({
  //     behavior: isMobile ? 'instant' : 'auto', // smooth | auto;
  //     block: 'center', // start | center | end | nearest;
  //   });
  //   if (isMobile) {
  //     // On mobile it would scroll parent panel too.
  //     // We have to correct this manually (Until exist cleaner solution)
  //     document
  //       .getElementsByClassName(BUYSELL_MOBILE_SCROLL_CLASS)?.[0]
  //       ?.scroll({ top: 0, behavior: 'instant' });
  //   }
  // }, [isMobile]);

  // useEffect(() => {
  //   setTimeout(() => {
  //     if (separatorRef.current) {
  //       scrollIntoCenter();
  //     } else {
  //       // Hotfix: Wait a ref
  //       setTimeout(scrollIntoCenter, 1000);
  //     }
  //   });
  // }, [currentMarket, scrollIntoCenter]);

  // useEffect(() => {
  //   // Center orderbook when user change tick size
  //   if (userTickSizeInDecimals) {
  //     scrollIntoCenter();
  //   }
  // }, [scrollIntoCenter, userTickSizeInDecimals]);

  const { askPriceLevelsArray, bidPriceLevelsArray } =
    useFetchAndSyncCurrentMarketOrderPrices({
      loggedIn,
      walletAddress,
      currentMarketPair: currentMarket.pair,
    });
  const processPriceLevels = useCallback(
    (levels: string[], side: 'ask' | 'bid') =>
      levels
        .map(price =>
          adjustPriceToTickSize(
            BigInt(
              BigNumberJs(price)
                .multipliedBy(10 ** EXCHANGE_DECIMALS)
                .toNumber()
            ),
            BigInt(userTickSize.inPip),
            side === 'ask' ? asksTickRoundingMode : bidsTickRoundingMode
          )
        )
        .map(price =>
          BigNumberJs(Number(price))
            .dividedBy(10 ** EXCHANGE_DECIMALS)
            .toFixed(userTickSize.visibleDecimals)
        ),
    [userTickSize]
  );
  const askUserOrdersPriceLevels = useMemo(
    () => processPriceLevels(askPriceLevelsArray, 'ask'),
    [askPriceLevelsArray, processPriceLevels]
  );

  const bidUserOrdersPriceLevels = useMemo(
    () => processPriceLevels(bidPriceLevelsArray, 'bid'),
    [bidPriceLevelsArray, processPriceLevels]
  );
  // useWindowHeight(() => scrollIntoCenter());

  const recalculateMaxTotal = useCallback(() => {
    const maxBidQuantityPip = Math.max(...bids.map(bid => bid.total));
    const maxAskQuantityPip = Math.max(...asks.map(ask => ask.total));
    return Math.max(maxBidQuantityPip, maxAskQuantityPip);
    // return (maxBidQuantityPip + maxAskQuantityPip) / 2;
  }, [asks, bids]);

  const [maxTotal, setMaxTotal] = useState<number>();

  useEffect(() => {
    // Initial calculation
    if (!maxTotal && bids.length > 0 && asks.length > 0) {
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setMaxTotal(recalculateMaxTotal);
    }
  }, [asks.length, bids.length, maxTotal, recalculateMaxTotal]);

  useActiveWindowInterval(() => {
    // Recalculate averages only once per 3 seconds, so we dont end up always end up re-rendering bars
    setMaxTotal(recalculateMaxTotal());
  }, 3000);

  const {
    asksDepthPercentages,
    asksTotalPercentages,
    bidsDepthPercentages,
    bidsTotalPercentages,
  } = useMemo(() => {
    const prepareValues = () => {
      let totalAsksDepthAmountPip = JSBI.BigInt('0');
      const askDepthsPip = asks
        .slice(0)
        .reverse()
        .map(ask => {
          totalAsksDepthAmountPip = JSBI.add(
            totalAsksDepthAmountPip,
            JSBI.multiply(ask.pricePip, ask.quantityPip)
          );
          return totalAsksDepthAmountPip;
        })
        .reverse();
      let totalBidsDepthAmountPip = JSBI.BigInt('0');
      const bidDepthsPip = bids.map(bid => {
        totalBidsDepthAmountPip = JSBI.add(
          totalBidsDepthAmountPip,
          JSBI.multiply(bid.pricePip, bid.quantityPip)
        );
        return totalBidsDepthAmountPip;
      });
      return {
        totalAsksDepthAmountPip,
        askDepthsPip,
        totalBidsDepthAmountPip,
        bidDepthsPip,
      };
    };
    const {
      totalAsksDepthAmountPip,
      askDepthsPip,
      totalBidsDepthAmountPip,
      bidDepthsPip,
    } = prepareValues();

    const maxDepthAmountPip = JSBI.GT(
      totalAsksDepthAmountPip,
      totalBidsDepthAmountPip
    )
      ? totalAsksDepthAmountPip
      : totalBidsDepthAmountPip;
    // const averageDepthAmountPip = JSBI.divide(JSBI.add(totalAsksDepthAmountPip, totalBidsDepthAmountPip), JSBI.BigInt(2));
    const asksDepthPercentages = askDepthsPip.map(depth =>
      JSBI.GT(maxDepthAmountPip, 0)
        ? JSBI.divide(
            JSBI.multiply(depth, JSBI.BigInt('100')),
            maxDepthAmountPip
          ).toString()
        : undefined
    );

    const bidsDepthPercentages = bidDepthsPip.map(depth =>
      JSBI.GT(maxDepthAmountPip, 0)
        ? JSBI.divide(
            JSBI.multiply(depth, JSBI.BigInt('100')),
            maxDepthAmountPip
          ).toString()
        : undefined
    );
    const asksTotalPercentages = asks.map(ask =>
      maxTotal && maxTotal > 0 ? (ask.total / maxTotal) * 100 : undefined
    );

    const bidsTotalPercentages = bids.map(bid =>
      maxTotal && maxTotal > 0 ? (bid.total / maxTotal) * 100 : undefined
    );

    return {
      bidsTotalPercentages,
      bidsDepthPercentages,
      asksTotalPercentages,
      asksDepthPercentages,
    };
  }, [asks, maxTotal, bids]);

  const spread = useMemo(
    () =>
      serviceExists ? getSpreadFromDisplayedOrderbook(asks, bids) : undefined,
    [asks, bids, serviceExists]
  );

  const { spreadPip, spreadPercentage, topBid, bottomAsk } = spread || {};

  if (spreadPip && JSBI.lessThan(spreadPip, JSBI.BigInt('0'))) {
    console.warn(
      `Negative spread details: asks=${asks.length} bids=${bids.length}, related ask/bid:`
    );
    console.warn(bottomAsk);
    console.warn(topBid);
    console.error(`Negative spread on market ${currentMarket.pair}`);
  }

  const dashboardMobileTradeUrl = `${getDashboardUrl(
    'katanaEVMChain',
    currentMarket.pair
  )}/mobile-trade`;

  const onBidClick = useCallback(
    (index: number) => {
      const bid = bids[index];
      console.log('Orderbook click on bid', bid);
      staticDispatch(prefillOrderInputPrice(bid.price.toString()));
      if (!isMobile) {
        return;
      }
      setTimeout(() => redirectTo(dashboardMobileTradeUrl), 500);
    },
    [bids, dashboardMobileTradeUrl, isMobile]
  );

  const onAskClick = useCallback(
    (index: number) => {
      const ask = asks[index];
      console.log('Orderbook click on ask', ask);
      staticDispatch(prefillOrderInputPrice(ask.price.toString()));
      if (!isMobile) {
        return;
      }
      setTimeout(() => redirectTo(dashboardMobileTradeUrl), 500);
    },
    [asks, isMobile, dashboardMobileTradeUrl]
  );

  const {
    hoveredRow,
    lastHoveredType,
    handleRowHoverExit,
    onRowHover,
    xOffset,
    xOffsetRef,
  } = useHoveredRow();

  const { rowsSumAsks, rowsSumBids } = useSummaryLists({
    asks,
    bids,
    type: lastHoveredType,
  });

  return (
    <>
      {summaryTooltipEnabled && hoveredRow && (
        <SummaryTooltip
          yOffset={hoveredRow.yPosition}
          xOffset={xOffset}
          rowsSumAsks={rowsSumAsks}
          rowsSumBids={rowsSumBids}
          selectedRow={hoveredRow}
        />
      )}
      <Asks
        ref={xOffsetRef}
        disabled={!orders.initialized}
        $height={orderbookSideHeightPx}
      >
        <AsksAbsolute>
          {asks.map((order, index) => (
            <OrderbookRow
              type="ask"
              key={order.price}
              order={order}
              index={index}
              onClick={onAskClick}
              userOrdersPriceLevels={askUserOrdersPriceLevels}
              isHovered={
                hoveredRow?.type === 'ask' && hoveredRow.index === index
              }
              onSetHoveredRow={onRowHover}
              onMouseLeave={handleRowHoverExit}
              totalPercentage={asksTotalPercentages?.[index]}
              depthPercentage={asksDepthPercentages?.[index]}
              summaryTooltipEnabled={summaryTooltipEnabled}
              minimalLayout={minimalLayout}
            />
            // </CSSTransition>
          ))}
          {/* </TransitionGroup> */}
        </AsksAbsolute>
      </Asks>
      <div>
        <SpreadComponentMemoized
          spreadPip={spreadPip}
          spreadPercentage={spreadPercentage}
          visibleDecimals={marketTickSizeVisibleDecimals}
          loading={!orders.initialized}
          minimalLayout={minimalLayout}
        />
      </div>
      <Bids disabled={!orders.initialized} $height={orderbookSideHeightPx}>
        <BidsAbsolute id={BIDS_WRAP_ID}>
          {/* <TransitionGroup className={BIDS_WRAP_CLASS}> */}
          {bids.map((order, index) => (
            // <CSSTransition
            //   key={order.price}
            //   timeout={appearAnimationIgnorePeriod ? 0 : TRANSITION_SPEED_MS}
            //   classNames={ROW_CLASS_NAME}
            // >
            <OrderbookRow
              type="bid"
              key={order.price}
              order={order}
              index={index}
              onClick={onBidClick}
              userOrdersPriceLevels={bidUserOrdersPriceLevels}
              isHovered={
                hoveredRow?.type === 'bid' && hoveredRow.index === index
              }
              onSetHoveredRow={onRowHover}
              onMouseLeave={handleRowHoverExit}
              totalPercentage={bidsTotalPercentages?.[index]}
              depthPercentage={bidsDepthPercentages?.[index]}
              summaryTooltipEnabled={summaryTooltipEnabled}
              minimalLayout={minimalLayout}
            />
            // </CSSTransition>
          ))}
          {/* </TransitionGroup> */}
        </BidsAbsolute>
      </Bids>
    </>
  );
};

export default OrderbookTableBody;
